/* eslint-disable no-empty */
import { createContext, useContext, useEffect, useState, useCallback } from 'react';

// Reuse the SAME backend auth endpoint as /gate33-program/ (same password as confirmed by user).
// We store the token under a dedicated key so the two programs remain independent
// from a session-management point of view, but the password check is shared.

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'g33_catering_token';

const CateringContext = createContext(null);

const getStoredToken = () => {
  try { return localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
};
const setStoredToken = (t) => {
  try { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); } catch {}
};

const apiFetch = async (path, opts = {}) => {
  const token = getStoredToken();
  const headers = { ...(opts.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (opts.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${API}${path}`, { ...opts, headers, credentials: 'include' });
  return res;
};

export const CateringProvider = ({ children }) => {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  // Probe existing session via the shared backend check endpoint
  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch('/api/gate33-program/check');
        if (r.ok) {
          const d = await r.json();
          if (d.authed) setAuthed(true);
        }
      } catch {}
      setChecking(false);
    })();
  }, []);

  const login = useCallback(async (password) => {
    const r = await apiFetch('/api/gate33-program/auth', {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
    if (!r.ok) {
      if (r.status === 401) throw new Error('Wrong password.');
      throw new Error(`Server error (${r.status}).`);
    }
    const d = await r.json();
    if (d.access_token) setStoredToken(d.access_token);
    setAuthed(true);
  }, []);

  const logout = useCallback(async () => {
    try { await apiFetch('/api/gate33-program/logout', { method: 'POST' }); } catch {}
    setStoredToken('');
    setAuthed(false);
  }, []);

  return (
    <CateringContext.Provider value={{ authed, checking, login, logout }}>
      {children}
    </CateringContext.Provider>
  );
};

export const useCatering = () => {
  const ctx = useContext(CateringContext);
  if (!ctx) throw new Error('useCatering must be used inside <CateringProvider>');
  return ctx;
};
